/**
 * SetVariableNode
 *
 * Sets one OR many variables in a single flow step. Each row picks a
 * target variable plus a value source:
 *
 *   1) "manual"   — author types a string. {{var}} placeholders are
 *                   interpolated at runtime via context.replaceVariables.
 *                   The result is always a string.
 *   2) "variable" — author picks ANOTHER variable as the source. The
 *                   target receives that variable's RAW runtime value
 *                   (object / array / number / string preserved), via
 *                   context.getVariable.
 *
 * Rows are written in order, so a later row can read what an earlier
 * row in the same node just wrote.
 *
 * Pure in-memory operation — no API calls, no waits — about as fast as
 * a node can be.
 *
 * Shape of node.data:
 *   {
 *     label: "Set Variable",
 *     items: [
 *       {
 *         id: "set_<unique>",
 *         variableName: "full_name",          // target
 *         source: "manual" | "variable",
 *         value: "{{first}} {{last}}",        // used when source === "manual"
 *         sourceVariable: ""                  // used when source === "variable"
 *       }
 *     ]
 *   }
 */
import React, { useState, useCallback, useRef } from "react";
import FlowNodeEditorShell from "../FlowBuilder/FlowNodeEditorShell";
import { useFlowNodeEdit } from "../../hooks/useFlowNodeEdit";
import { getNodeEditorMeta } from "../../utils/flowNodeEditorMeta";
import { Handle, Position } from "reactflow";
import {
  Pencil,
  Check,
  Plus,
  Trash2,
  Variable,
  ArrowRight,
  PenSquare,
} from "lucide-react";
import VariableDropdown from "../FlowBuilder/VariableDropdown";
import NodeActionButtons from "./NodeActionButtons";
import NodeEditorTabs from "./NodeEditorTabs";

// ─── Constants ────────────────────────────────────────────────
const MAX_ITEMS = 20;

const SOURCE_MANUAL = "manual";
const SOURCE_VARIABLE = "variable";

// ─── Helpers ──────────────────────────────────────────────────
const newItemId = () => `set_${Date.now()}_${Math.random().toString(36).slice(2, 7)}`;

const makeBlankItem = () => ({
  id: newItemId(),
  variableName: "",
  source: SOURCE_MANUAL,
  value: "",
  sourceVariable: "",
});

/** A short, friendly summary of an item for the collapsed card. */
const summariseItem = (item) => {
  if (!item) return { target: "?", arrow: "←", source: "(empty)" };
  const target = item.variableName ? `{{${item.variableName}}}` : "(unset)";
  if (item.source === SOURCE_VARIABLE) {
    const src = item.sourceVariable ? `{{${item.sourceVariable}}}` : "(no source)";
    return { target, source: src };
  }
  const literal = (item.value || "").trim();
  if (!literal) return { target, source: '""' };
  // Truncate long literals so the card stays compact
  const short = literal.length > 24 ? literal.slice(0, 21) + "…" : literal;
  return { target, source: `"${short}"` };
};

// ─── Collapsed View ───────────────────────────────────────────
const CollapsedView = ({ data, onEdit }) => {
  const items = data.items || [];

  return (
    <div
      className="relative rounded-xl overflow-hidden"
      style={{ backgroundColor: "#ECFDF5", padding: "10px", minWidth: "240px", maxWidth: "300px" }}
    >
      <button
        onClick={(e) => { e.stopPropagation(); onEdit(); }}
        className="absolute top-2 right-2 z-10 p-1.5 bg-white/90 hover:bg-white rounded-full shadow-md transition-all hover:shadow-lg"
        title="Edit variables"
      >
        <Pencil size={12} className="text-emerald-600" />
      </button>

      <div className="bg-white rounded-lg shadow-sm p-3">
        {/* Title row */}
        <div className="flex items-center gap-2 mb-2">
          <div className="p-1.5 bg-emerald-100 rounded-lg">
            <PenSquare size={14} className="text-emerald-600" />
          </div>
          <span className="text-[12px] font-bold text-emerald-900">Set Variable</span>
          {items.length > 0 && (
            <span className="ml-auto text-[9px] font-semibold text-emerald-700 bg-emerald-50 px-1.5 py-0.5 rounded-full uppercase tracking-wider">
              {items.length === 1 ? "1 var" : `${items.length} vars`}
            </span>
          )}
        </div>

        {/* Body */}
        {items.length === 0 ? (
          <p className="text-[11px] text-gray-400 italic">No variables set</p>
        ) : (
          <div className="space-y-1">
            {items.slice(0, 3).map((item) => {
              const sum = summariseItem(item);
              return (
                <div key={item.id} className="flex items-center gap-1 text-[10px] text-gray-700">
                  <span className="font-mono font-semibold text-emerald-700 truncate max-w-[80px]" title={sum.target}>
                    {sum.target}
                  </span>
                  <ArrowRight size={9} className="text-gray-400 flex-shrink-0" />
                  <span className="font-mono text-gray-600 truncate flex-1" title={sum.source}>
                    {sum.source}
                  </span>
                </div>
              );
            })}
            {items.length > 3 && (
              <p className="text-[10px] text-emerald-700 font-semibold">
                +{items.length - 3} more
              </p>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

// ─── Single Item Row ──────────────────────────────────────────
const ItemRow = ({ index, item, onChange, onRemove, flowVariables, onCreateVariable }) => {
  const valueInputRef = useRef(null);

  const update = (field, value) => onChange({ ...item, [field]: value });

  const insertVarInValue = (varName) => {
    if (!varName) return;
    const cur = item.value || "";
    const input = valueInputRef.current;
    const pos = input?.selectionStart ?? cur.length;
    update("value", cur.slice(0, pos) + `{{${varName}}}` + cur.slice(pos));
  };

  const isVariable = item.source === SOURCE_VARIABLE;

  return (
    <div className="bg-white rounded-lg border border-emerald-100 p-2 space-y-1.5">
      {/* Row header: index + target dropdown + remove */}
      <div className="flex items-center gap-1.5">
        <span className="text-[10px] font-bold text-emerald-700 bg-emerald-50 px-1.5 py-0.5 rounded flex-shrink-0">
          #{index + 1}
        </span>
        <div className="flex-1 min-w-0">
          <VariableDropdown
            value={item.variableName}
            onChange={(name) => update("variableName", name || "")}
            flowVariables={flowVariables || []}
            allowCreate
            onCreateVariable={onCreateVariable}
            placeholder="Pick or create…"
          />
        </div>
        <button
          onClick={onRemove}
          className="p-1 text-gray-400 hover:text-red-500 rounded transition-colors flex-shrink-0"
          title="Remove row"
        >
          <Trash2 size={12} />
        </button>
      </div>

      {/* Source toggle */}
      <div className="bg-[#F7F7F7] rounded-md p-0.5 flex gap-0.5">
        <button
          onClick={() => update("source", SOURCE_MANUAL)}
          className={`flex-1 py-1 text-[10px] font-bold rounded transition-colors ${
            !isVariable
              ? "bg-emerald-600 text-white shadow-sm"
              : "text-gray-600 hover:bg-emerald-50"
          }`}
        >
          Manual
        </button>
        <button
          onClick={() => update("source", SOURCE_VARIABLE)}
          className={`flex-1 py-1 text-[10px] font-bold rounded transition-colors ${
            isVariable
              ? "bg-emerald-600 text-white shadow-sm"
              : "text-gray-600 hover:bg-emerald-50"
          }`}
        >
          From variable
        </button>
      </div>

      {/* Value input (manual) OR source dropdown (variable) */}
      {isVariable ? (
        <VariableDropdown
          value={item.sourceVariable}
          onChange={(name) => update("sourceVariable", name || "")}
          flowVariables={flowVariables || []}
          allowCreate
          onCreateVariable={onCreateVariable}
          placeholder="Pick source variable…"
        />
      ) : (
        <div className="flex items-center gap-1">
          <input
            ref={valueInputRef}
            type="text"
            value={item.value}
            onChange={(e) => update("value", e.target.value)}
            placeholder='Type a value, e.g. "active" or {{name}}'
            className="flex-1 px-2 py-1.5 text-[11px] bg-[#F0F0F0] rounded-lg border-0 focus:outline-none focus:ring-1 focus:ring-emerald-400 placeholder:text-gray-400"
          />
          <VariableDropdown
            value=""
            onChange={(name) => { if (name) insertVarInValue(name); }}
            flowVariables={flowVariables || []}
            allowCreate
            onCreateVariable={onCreateVariable}
            placeholder="Var"
            className="w-20"
          />
        </div>
      )}

      {/* Hint when manual value contains a variable placeholder */}
      {!isVariable && item.value && item.value.includes("{{") && (
        <div className="flex items-center gap-1.5 px-2 py-1 bg-emerald-50 rounded text-[9px] text-emerald-700">
          <Variable size={9} className="flex-shrink-0" />
          <span>Resolved as a string at run time.</span>
        </div>
      )}
    </div>
  );
};

// ─── Expanded Editor ──────────────────────────────────────────
const ExpandedEditor = ({ data, onSave, onCancel, flowVariables, onCreateVariable }) => {
  const [form, setForm] = useState(() => ({
    items: (data.items && data.items.length > 0)
      ? data.items.map((it) => ({
          id: it.id || newItemId(),
          variableName: it.variableName || "",
          source: it.source === SOURCE_VARIABLE ? SOURCE_VARIABLE : SOURCE_MANUAL,
          value: it.value || "",
          sourceVariable: it.sourceVariable || "",
        }))
      : [makeBlankItem()],
  }));

  const updateItem = (idx, next) => {
    setForm((p) => ({
      ...p,
      items: p.items.map((it, i) => (i === idx ? next : it)),
    }));
  };

  const removeItem = (idx) => {
    setForm((p) => ({
      ...p,
      items: p.items.filter((_, i) => i !== idx),
    }));
  };

  const addItem = () => {
    setForm((p) => {
      if (p.items.length >= MAX_ITEMS) return p;
      return { ...p, items: [...p.items, makeBlankItem()] };
    });
  };

  const handleSave = () => {
    // Persist a clean shape — strip any UI-only fields per row, normalise source.
    const cleaned = form.items.map((it) => ({
      id: it.id || newItemId(),
      variableName: (it.variableName || "").trim(),
      source: it.source === SOURCE_VARIABLE ? SOURCE_VARIABLE : SOURCE_MANUAL,
      value: it.source === SOURCE_VARIABLE ? "" : (it.value || ""),
      sourceVariable: it.source === SOURCE_VARIABLE ? (it.sourceVariable || "").trim() : "",
    }));
    onSave({ items: cleaned });
  };

  return (
    <div
      className="nowheel"
      style={{ backgroundColor: "#ECFDF5", borderRadius: "12px", padding: "14px", minWidth: "360px", maxWidth: "420px" }}
      onClick={(e) => e.stopPropagation()}
    >
      {/* Title bar */}
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <div className="p-1.5 bg-emerald-200 rounded-lg">
            <PenSquare size={14} className="text-emerald-700" />
          </div>
          <span className="text-[13px] font-bold text-emerald-900">Set Variable</span>
        </div>
        <div className="flex gap-1.5">
          <button
            onClick={handleSave}
            className="px-3 py-1.5 text-[11px] font-bold bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 transition-colors flex items-center gap-1 shadow-sm"
          >
            <Check size={12} /> Save
          </button>
          <button
            onClick={onCancel}
            className="px-3 py-1.5 text-[11px] font-bold bg-white text-gray-600 rounded-lg hover:bg-gray-100 transition-colors border border-gray-200"
          >
            Cancel
          </button>
        </div>
      </div>

      {/* Helper text */}
      <p className="text-[10px] text-gray-500 leading-snug mb-2">
        Set one or many variables. Rows are written in order, so a later row can use a value the
        previous row just set.
      </p>

      <NodeEditorTabs nodeType="setVariable">
      {/* Item rows */}
      <div className="space-y-2 mb-3">
        {form.items.map((item, idx) => (
          <ItemRow
            key={item.id}
            index={idx}
            item={item}
            onChange={(next) => updateItem(idx, next)}
            onRemove={() => removeItem(idx)}
            flowVariables={flowVariables}
            onCreateVariable={onCreateVariable}
          />
        ))}

        {form.items.length === 0 && (
          <div className="bg-white rounded-lg p-3 text-center border border-dashed border-emerald-200">
            <p className="text-[11px] text-gray-500">No rows yet. Click "Add row" to get started.</p>
          </div>
        )}

        <button
          onClick={addItem}
          disabled={form.items.length >= MAX_ITEMS}
          className="w-full py-1.5 text-[11px] font-bold text-emerald-700 border border-dashed border-emerald-300 rounded-lg hover:bg-emerald-50 transition-colors disabled:opacity-40 disabled:cursor-not-allowed flex items-center justify-center gap-1"
        >
          <Plus size={12} />
          Add row ({form.items.length}/{MAX_ITEMS})
        </button>
      </div>

      {/* Bottom save / cancel */}
      </NodeEditorTabs>
      <div className="flex gap-2">
        <button
          onClick={handleSave}
          className="flex-1 py-2 text-[12px] font-bold bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 transition-colors flex items-center justify-center gap-1.5 shadow-sm"
        >
          <Check size={14} /> Save
        </button>
        <button
          onClick={onCancel}
          className="flex-1 py-2 text-[12px] font-bold bg-white text-gray-600 rounded-lg hover:bg-gray-100 transition-colors border border-gray-200"
        >
          Cancel
        </button>
      </div>
    </div>
  );
};

// ─── Main Node ────────────────────────────────────────────────
const SetVariableNode = ({ id, data }) => {
  const { isEditing, openEditor, closeEditor } = useFlowNodeEdit(id, data);
  const editorMeta = getNodeEditorMeta("setVariable");

  const handleSave = useCallback(
    (formData) => {
      if (data.onNodeDataChange) {
        data.onNodeDataChange(id, { ...data, ...formData });
      }
      closeEditor();
    },
    [id, data, closeEditor]);

  return (
    <div
      className={`relative transition-all shadow-md`}
      style={{ borderRadius: "12px" }}
    >
      <NodeActionButtons id={id} data={data} />

      <Handle
        type="target"
        position={Position.Left}
        className="!w-3.5 !h-3.5 !bg-emerald-500 !border-2 !border-white !shadow-sm"
        style={{ left: -7 }}
      />

      <CollapsedView data={data} onEdit={openEditor} />
      <FlowNodeEditorShell
        open={isEditing}
        onClose={closeEditor}
        title={data.label || editorMeta.label}
        subtitle={editorMeta.label}
        icon={editorMeta.icon}
        accent={editorMeta.accent}
      >
        <ExpandedEditor
          data={data}
          onSave={handleSave}
          onCancel={() => closeEditor()}
          flowVariables={data.flowVariables || []}
          onCreateVariable={data.onCreateVariable}
         presentation="fullscreen" onCancel={closeEditor} />
      </FlowNodeEditorShell>

      <Handle
        type="source"
        position={Position.Right}
        className="!w-3.5 !h-3.5 !bg-emerald-500 !border-2 !border-white !shadow-sm"
        style={{ right: -7 }}
      />
    </div>
  );
};

export default SetVariableNode;
